import { apiBaseUrl } from '@/shared/env'
import {
  clearKitchenSession,
  getKitchenSession,
} from '@/shared/auth/sessionStorage'
import {
  clearDelmanSession,
  getDelmanSession,
} from '@/shared/auth/delmanSessionStorage'
import { clearDelmanDevice } from '@/shared/auth/delmanDeviceStorage'
import { clearKitchenDevice } from '@/shared/auth/deviceStorage'
import { setDeviceRevokedNotice } from '@/shared/auth/deviceRevokedNotice'
import { clearDeviceUid } from '@/shared/auth/deviceUid'

const baseUrl = apiBaseUrl

function isWrongPinError(message: string): boolean {
  const lower = message.toLowerCase()
  return lower.includes('pin salah') || lower.includes('terlalu banyak percobaan pin')
}

function isDelmanApp(): boolean {
  return typeof window !== 'undefined' && window.location.pathname.startsWith('/delman')
}

export function getAccessToken(): string | null {
  if (typeof window === 'undefined') return null
  if (isDelmanApp()) {
    return getDelmanSession()?.accessToken ?? null
  }
  return getKitchenSession()?.accessToken ?? null
}

export function authHeader(): Record<string, string> {
  const token = getAccessToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/**
 * Token kedaluwarsa / tidak valid → bersihkan sesi sesuai konteks dan arahkan
 * ke halaman login yang tepat. Tidak dijalankan di server, saat sudah di halaman
 * login, atau untuk permintaan auth (mis. PIN salah saat login).
 */
function handleUnauthorized(): void {
  if (typeof window === 'undefined') return
  const path = window.location.pathname
  const onAuthPage = path.includes('/login') || path.includes('/activate')
  const isDelman = path.startsWith('/delman')

  if (isDelman) {
    clearDelmanSession()
    if (!onAuthPage) window.location.href = '/delman/login'
  } else {
    clearKitchenSession()
    if (!onAuthPage) window.location.href = '/kitchen/login'
  }
}

export const DEVICE_REVOKED_CODE = 'DEVICE_REVOKED'

export type ApiErrorBody = {
  error?: string
  code?: string
  [key: string]: unknown
}

/** Error API yang mempertahankan HTTP status, `code`, dan body (mis. snapshot absensi). */
export class ApiError extends Error {
  readonly status: number
  readonly code?: string
  readonly body: ApiErrorBody | null

  constructor(message: string, status: number, body: ApiErrorBody | null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = typeof body?.code === 'string' ? body.code : undefined
    this.body = body
  }
}

/** Binding perangkat dicabut di server → sesi dan binding lokal tidak berlaku lagi. */
function handleDeviceRevoked(): void {
  if (typeof window === 'undefined') return
  setDeviceRevokedNotice()
  if (window.location.pathname.startsWith('/delman')) {
    clearDelmanSession()
    clearDelmanDevice()
    clearDeviceUid()
    if (!window.location.pathname.includes('/activate')) window.location.href = '/delman/activate'
  } else {
    clearKitchenSession()
    clearKitchenDevice()
    clearDeviceUid()
    if (!window.location.pathname.includes('/activate')) window.location.href = '/kitchen/activate'
  }
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...authHeader(),
      ...init?.headers,
    },
  })

  if (!res.ok) {
    let message = `API ${res.status}: ${res.statusText}`
    let body: ApiErrorBody | null = null
    try {
      body = (await res.json()) as ApiErrorBody
      if (typeof body.error === 'string' && body.error) message = body.error
    } catch {
      // keep default message
    }
    const error = new ApiError(message, res.status, body)

    if (res.status === 401 && error.code === DEVICE_REVOKED_CODE) {
      handleDeviceRevoked()
    } else if (res.status === 401 && !path.startsWith('/auth') && !isWrongPinError(message)) {
      // Hanya logout saat token/sesi benar-benar invalid — bukan PIN salah di modal konfirmasi.
      handleUnauthorized()
    }

    throw error
  }

  return res.json() as Promise<T>
}
